import { z } from 'zod';
import { COURSE_CATEGORIES, type CourseCategory } from '../constants/categories.js';
import { AQF_LEVELS, type AqfLevel } from '../constants/aqf-levels.js';
import { AU_STATES, type AuState } from '../constants/au-states.js';

const categoryEnum = z.enum(COURSE_CATEGORIES as unknown as [CourseCategory, ...CourseCategory[]]);
const aqfEnum = z.enum(AQF_LEVELS as unknown as [AqfLevel, ...AqfLevel[]]);
const stateEnum = z.enum(AU_STATES as unknown as [AuState, ...AuState[]]);

/**
 * Normalised consultant course-search query. All fields optional and combined
 * with AND. The wire format (URL query / HTTP query string) is comma-separated
 * for multi-value fields and plain scalars otherwise — `normalizeSearchParams`
 * turns that raw shape into this validated object on both the web and the API.
 */
export const SearchCoursesQuerySchema = z
  .object({
    q: z.string().trim().min(1).max(120).optional(),
    category: z.array(categoryEnum).min(1).optional(),
    aqf: z.array(aqfEnum).min(1).optional(),
    state: z.array(stateEnum).min(1).optional(),
    intake: z.array(z.number().int().min(1).max(12)).min(1).optional(),
    /** Max IELTS overall band (matches courses with ielts_overall ≤ this). */
    ieltsMax: z.number().min(0).max(9).optional(),
    /** Fee bounds in whole AUD dollars (annual tuition). */
    feeMin: z.number().int().min(0).optional(),
    feeMax: z.number().int().min(0).optional(),
    /** PR pathway: any CSOL/MLTSSL occupation-list membership. */
    pr: z.boolean().optional(),
    /** A single ANZSCO occupation code the course must list. */
    anzsco: z.string().trim().min(1).max(12).optional(),
  })
  .strict();

export type SearchCoursesQuery = z.infer<typeof SearchCoursesQuerySchema>;

/** True when no search term and no filters are set (an empty query). */
export function isEmptySearchQuery(query: SearchCoursesQuery): boolean {
  return (
    !query.q &&
    !query.category &&
    !query.aqf &&
    !query.state &&
    !query.intake &&
    query.ieltsMax === undefined &&
    query.feeMin === undefined &&
    query.feeMax === undefined &&
    query.pr === undefined &&
    !query.anzsco
  );
}

function asString(v: unknown): string | undefined {
  if (typeof v === 'string') {
    const t = v.trim();
    return t.length ? t : undefined;
  }
  return undefined;
}

function asList(v: unknown): string[] | undefined {
  if (Array.isArray(v)) {
    const items = v.map((x) => String(x).trim()).filter(Boolean);
    return items.length ? items : undefined;
  }
  const s = asString(v);
  if (!s) return undefined;
  const items = s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return items.length ? items : undefined;
}

function asNumber(v: unknown): number | undefined {
  const s = typeof v === 'number' ? String(v) : asString(v);
  if (s === undefined) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The raw wire bag before any field is read. Brief API-2: the API controller
 * used to reach this function through an unchecked `req.query as Record<...>`
 * cast, which is the one step that was not Zod. Taking `unknown` and validating
 * the container here means the cast is gone and the whole path — container then
 * fields — is Zod.
 */
const RawQueryBagSchema = z.record(z.unknown());

/**
 * Turn a raw query/search-params bag (string | string[] values from a URL or an
 * Express `req.query`) into a validated `SearchCoursesQuery`. Shared by the web
 * route loader and the API controller so both sides agree on the wire format.
 * Throws (Zod) on a non-object bag or values outside the allowed sets.
 */
export function normalizeSearchParams(rawInput: unknown): SearchCoursesQuery {
  const raw = RawQueryBagSchema.parse(rawInput);
  const intakeList = asList(raw.intake)
    ?.map((x) => Number(x))
    .filter((n) => Number.isInteger(n));

  const candidate = {
    q: asString(raw.q),
    category: asList(raw.category),
    aqf: asList(raw.aqf),
    state: asList(raw.state),
    intake: intakeList && intakeList.length ? intakeList : undefined,
    ieltsMax: asNumber(raw.ieltsMax),
    feeMin: asNumber(raw.feeMin),
    feeMax: asNumber(raw.feeMax),
    pr: raw.pr === 'true' || raw.pr === true ? true : undefined,
    anzsco: asString(raw.anzsco),
  };

  // Drop undefined keys so `.strict()` sees only the fields that are set.
  const cleaned: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(candidate)) {
    if (v !== undefined) cleaned[k] = v;
  }
  return SearchCoursesQuerySchema.parse(cleaned);
}
